// What the agents work from, read-only: the regulatory articles and their
// criteria, the section catalogue, the drafting configuration and rules.
import { useState } from "react";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { indicatorLabel } from "../components/drafting";
import { VersionedCell } from "../components/referential";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { w } from "../i18n";

type Tab = "articles" | "sections" | "drafting";
const TABS: Tab[] = ["articles", "sections", "drafting"];

export function ReferentialPage() {
  const data = useResource("referential", () => api.referential());
  const [tab, setTab] = useState<Tab>("articles");
  const ref = data.data;
  return <>
    <PageHeader eyebrow={w("ref.eyebrow")} title={w("ref.title")} lead={w("ref.lead")} />
    {data.error && <ErrorState title={w("common.genericError")} error={data.error} onRetry={data.reload} />}
    {data.loading && <SkeletonBlock lines={10} />}
    {ref && <>
      <div className="ws-tabs" role="tablist">{TABS.map(item => {
        const pending = item === "articles" ? ref.articles.filter(a => a.pending).length : item === "sections" ? ref.sections.filter(s => s.pending).length : 0;
        return <button key={item} role="tab" className="ws-tab" aria-selected={tab === item} onClick={() => setTab(item)}>
          {w(`ref.tab.${item}`)}{pending > 0 && <span className="ws-tab-badge">{w("refv.pendingCount", { count: pending })}</span>}</button>;
      })}</div>

      {tab === "articles" && <div className="ws-card ws-table-wrap"><table className="ws-table ws-audit-table">
        <thead><tr><th>{w("ref.col.reference")}</th><th>{w("ref.col.title")}</th><th>{w("ref.col.criteria")}</th>
          <th>{w("ref.col.sections")}</th><th>{w("ref.col.control")}</th></tr></thead>
        <tbody>{ref.articles.map(a => <tr key={a.id}>
          <td className="ws-mono">{a.code}</td><td><strong>{a.title}</strong><div className="ws-small ws-muted">{a.description}</div></td>
          <td><VersionedCell kind="article" itemKey={a.id} label={`${a.code} ${a.title}`} current={a.criteria} version={a.version}
            pending={a.pending} /></td><td className="ws-mono ws-small">{a.sections.join(", ") || "-"}</td>
          <td><StatusBadge status={a.automated ? "ready" : "needs_review"} label={w(a.automated ? "ref.automated" : "ref.manual")} /></td>
        </tr>)}</tbody></table></div>}

      {tab === "sections" && <div className="ws-card ws-table-wrap"><table className="ws-table ws-audit-table">
        <thead><tr><th>{w("ref.col.block")}</th><th>{w("ref.col.kind")}</th><th>{w("ref.col.instructions")}</th>
          <th>{w("ref.col.inputs")}</th><th>{w("ref.col.checks")}</th></tr></thead>
        <tbody>{ref.sections.map(s => <tr key={s.code}>
          <td><span className="ws-mono">{s.code === "SYN" ? "" : s.code}</span> {s.title}</td>
          <td className="ws-small">{w(`plan.kind.${s.kind}`)}</td>
          <td><VersionedCell kind="section" itemKey={s.code} label={`${s.code === "SYN" ? "" : s.code} ${s.title}`}
            current={s.instructions} version={s.version} pending={s.pending} /></td>
          <td className="ws-small">{s.required_facts.length > 0 && <div><strong>{w("ref.required")}</strong> {s.required_facts.map(indicatorLabel).join(", ")}</div>}
            {s.templates.length > 0 && <div className="ws-muted">QRT {s.templates.join(", ")}</div>}
            {s.document_types.length > 0 && <div className="ws-muted">{s.document_types.join(", ")}</div>}</td>
          <td className="ws-mono ws-small">{s.checks.join(", ") || "-"}</td>
        </tr>)}</tbody></table></div>}

      {tab === "drafting" && <div className="ws-stack">
        <section className="ws-card">
          <div className="ws-card-head"><h2>{w("ref.config")}</h2>
            <StatusBadge status={ref.drafting.configured ? "ready" : "missing"} label={w(ref.drafting.configured ? "ref.configured" : "ref.notConfigured")} /></div>
          <dl className="ws-dl ws-card-pad">
            <dt>{w("ref.mode")}</dt><dd>{ref.drafting.mode}</dd>
            <dt>{w("ref.deployment")}</dt><dd className="ws-mono">{ref.drafting.deployment}</dd>
            <dt>{w("ref.promptVersion")}</dt><dd className="ws-mono">{ref.drafting.prompt_version}</dd>
            <dt>{w("ref.guardrails")}</dt><dd>{ref.drafting.guardrails.map(g => w(`ref.guardrail.${g}`)).join(" · ")}</dd>
          </dl>
        </section>
        {Object.entries(ref.drafting.rules).map(([kind, rule]) => <section key={kind} className="ws-card">
          <div className="ws-card-head"><h2>{w("ref.rule", { kind: w(`plan.kind.${kind}`) })}</h2></div>
          <p className="ws-card-pad ws-small" style={{ margin: 0 }}>{rule}</p></section>)}
      </div>}
      <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("ref.note")}</Notice></div>
    </>}
  </>;
}
